import cn from 'classnames';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';

import Button from 'components/common/Button';
import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import Checkmark from 'components/icons/Checkmark';
import { UserContext } from 'components/signUpFunnel/Contexts';
import { HOME_CHEF } from 'context/BrandContext';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useFunnelNavigation from 'hooks/useFunnelNavigation';
import { FAMILY, STANDARD } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { updateMealPlan } from 'services/RequestService/endpoints/signUp';
import { updateStatus } from 'services/RequestService/endpoints/user';
import styles from 'styles/components/signUpFunnel/pickPlan/planCards';

import PlanCardData from './PlanCardData';

const PlanFamilyCards = ({ setError, mealPlanSlug, recommendedMealPlan, isFamilyMealPlan }) => {
  const { setUser } = useContext(UserContext);
  const { nextStep } = useFunnelNavigation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onSubmit = useAnalyticsCallback(
    async ({ mealPlan }) => {
      setIsSubmitting(true);
      const response = await updateMealPlan({
        params: { mealPlanSlug: mealPlan },
      });
      if (response.ok) {
        const statusResponse = await updateStatus({
          params: {
            status: nextStep.status,
          },
        });
        setUser(prevUser => ({
          ...prevUser,
          mealPlanSlug: mealPlan,
          status: statusResponse.user?.status || nextStep.status,
        }));

        setIsSubmitting(false);
        const newFunnelRoute =
          mealPlan !== mealPlanSlug
            ? nextStep.route.replace(`/${mealPlanSlug}`, `/${mealPlan}`)
            : nextStep.route;
        navigate(newFunnelRoute);
      } else {
        setIsSubmitting(false);
        setError(response.error);
        ErrorLogging.captureException(response.error);
      }
    },
    ({ logEvent, events, callbackArgs }) => {
      const mealPlan = callbackArgs[0]?.mealPlan;
      const planSubmittedEvent = isFamilyMealPlan ? 'plans_family' : 'plans_core';
      logEvent(events.signUp[HOME_CHEF][planSubmittedEvent].submitted, { mealPlan });
    },
    [nextStep]
  );

  const Card = ({ mealPlan = STANDARD, isRecommended = false }) => {
    return (
      <div
        className={cn('relative mx-auto mb-6 gap-2 rounded-lg bg-white-100 p-4 shadow-lg', {
          [styles.planCardStandard]: mealPlan === STANDARD,
          [styles.planCardFamily]: mealPlan === FAMILY,
        })}
      >
        {isRecommended && (
          <div className="absolute -top-3 left-2 rounded-full border border-solid border-lemon-100 bg-lemon-70 px-2 py-1">
            <Text brandStylesOverrides="h7">Recommended for you</Text>
          </div>
        )}
        <div className="flex h-full flex-col justify-between">
          <div>
            <img
              className={cn('w-28', { 'pt-1.5': mealPlan === FAMILY })}
              src={PlanCardData[mealPlan].logo}
              alt="Logo"
            />
            <Heading htmlElement="h3" brandStylesOverrides="h7" className="pt-2 text-charcoal-90">
              {PlanCardData[mealPlan].subheader}
            </Heading>
          </div>
          <div className="px-2 pt-6">
            {PlanCardData[mealPlan].pointers.map((text, index) => (
              <div key={`pointer-${index}`} className="flex flex-row pb-3 text-left">
                <Checkmark color="green" />
                <Text brandStylesOverrides="textSm font-medium text-charcoal-100" className="ml-2">
                  {text}
                </Text>
              </div>
            ))}
          </div>
          <Button
            className="mt-3 w-full px-2 py-3 text-base"
            onClick={() => onSubmit({ mealPlan: mealPlan })}
            disabled={isSubmitting}
            variation="Pill"
          >
            {isSubmitting ? 'Submitting...' : PlanCardData[mealPlan].ctaText}
          </Button>
        </div>
      </div>
    );
  };

  Card.propTypes = {
    mealPlan: PropTypes.string,
    isRecommended: PropTypes.bool,
  };

  return (
    <div className="block bp2:flex bp2:flex-row bp2:gap-x-6">
      {mealPlanSlug === STANDARD && recommendedMealPlan !== FAMILY ? (
        <>
          <Card mealPlan={STANDARD} />
          <Card mealPlan={FAMILY} />
        </>
      ) : (
        <>
          <Card mealPlan={FAMILY} isRecommended={recommendedMealPlan === FAMILY} />
          <Card mealPlan={STANDARD} />
        </>
      )}
    </div>
  );
};

PlanFamilyCards.propTypes = {
  setError: PropTypes.func,
  mealPlanSlug: PropTypes.string,
  recommendedMealPlan: PropTypes.string,
  isFamilyMealPlan: PropTypes.bool,
};

export default PlanFamilyCards;
